import { useState, useEffect, useRef, useCallback } from 'react';
import { diaryAPI, walkInAPI, roomsAPI, guestsAPI, bookingsAPI } from '../../lib/api';
import { getCurrencySymbol } from '../../lib/currencies';
import { AdminLayout } from '../../components/AdminLayout';
import { useProperty } from '../../contexts/PropertyContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  ChevronLeft, ChevronRight, Loader2, PlusCircle,
  Maximize2, ChevronDown, ChevronUp, SlidersHorizontal, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { BookingActionModal } from '../../components/BookingActionModal';

function todayISO() { return new Date().toISOString().split('T')[0]; }
function addDaysISO(d, n) { const x = new Date(d+'T00:00:00'); x.setDate(x.getDate()+n); return x.toISOString().split('T')[0]; }
function daysBetween(a, b) { return Math.max(1, Math.round((new Date(b+'T00:00:00') - new Date(a+'T00:00:00'))/86400000)); }
function monthStartISO() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; }
function monthEndISO() { const d = new Date(); const last = new Date(d.getFullYear(), d.getMonth()+1, 0); return last.toISOString().split('T')[0]; }

const BAR_COLORS = {
  past:    { bg: '#e0e0e0', text: '#777', border: '#ccc' },
  future:  { bg: '#bbdefb', text: '#1565c0', border: '#90caf9' },
  current: { bg: '#c8e6c9', text: '#2e7d32', border: '#a5d6a7' },
  expired: { bg: '#ffcdd2', text: '#c62828', border: '#ef9a9a' },
};

const COL_W = 60;
const ROOM_COL_W = 140;

/* ════════════════ Booking Bar ════════════════ */
function BookingBar({ booking, startDate, totalDays, colWidth, onClick }) {
  const rangeStart = new Date(startDate+'T00:00:00');
  const barStart = new Date(booking.check_in+'T00:00:00');
  const barEnd = new Date(booking.check_out+'T00:00:00');
  const rangeEnd = new Date(rangeStart); rangeEnd.setDate(rangeEnd.getDate()+totalDays);
  const effectiveStart = barStart < rangeStart ? rangeStart : barStart;
  const effectiveEnd = barEnd > rangeEnd ? rangeEnd : barEnd;
  const startOffset = Math.round((effectiveStart - rangeStart)/86400000);
  const barDays = Math.round((effectiveEnd - effectiveStart)/86400000);
  if (barDays <= 0) return null;

  const left = startOffset * colWidth;
  const width = barDays * colWidth - 2;
  const c = BAR_COLORS[booking.bar_status] || BAR_COLORS.future;

  return (
    <div
      className="absolute top-[3px] h-[28px] rounded-md flex items-center px-2.5 overflow-hidden cursor-pointer hover:brightness-90 transition-all shadow-sm"
      style={{ left: `${left+1}px`, width: `${width}px`, background: c.bg, color: c.text, border: `1px solid ${c.border}` }}
      title={`${booking.guest_name}${booking.room_number ? ` (Rm ${booking.room_number})` : ''}\n${booking.check_in} → ${booking.check_out}\n${booking.nights} nights`}
      onClick={() => onClick(booking)}
      data-testid={`booking-bar-${booking.id}`}
    >
      <span className="text-[11px] font-semibold truncate">{booking.guest_name}</span>
    </div>
  );
}

/* ════════════════ AVL Cell ════════════════ */
function AVLCell({ booked, total, isToday }) {
  const isFullyOccupied = booked >= total && booked > 0;
  const isOverbooked = booked > total;

  let bgClass = isToday ? 'bg-blue-50' : '';
  let textClass = 'text-gray-600';

  if (isOverbooked) { bgClass = 'bg-red-50'; textClass = 'text-green-600 font-bold'; }
  else if (isFullyOccupied) { bgClass = 'bg-orange-50'; textClass = 'text-red-500 font-bold'; }

  return (
    <div className={`flex-shrink-0 border-r border-gray-100 flex items-center justify-center ${bgClass}`} style={{ width: `${COL_W}px`, height: '34px' }}>
      <span className={`text-[11px] font-semibold ${textClass}`}>{booked}/{total}</span>
    </div>
  );
}

/* ════════════════ Timeline Grid ════════════════ */
function TimelineGrid({ diary, startDate, daysCount, onBookingClick, onLoadMoreRight, onLoadMoreLeft }) {
  const scrollRef = useRef(null);
  const today = todayISO();
  const [collapsed, setCollapsed] = useState({});

  const todayOffset = (() => {
    const diff = Math.round((new Date(today+'T00:00:00') - new Date(startDate+'T00:00:00'))/86400000);
    return (diff >= 0 && diff < daysCount) ? diff : -1;
  })();

  const toggleCollapse = (roomId) => setCollapsed(prev => ({ ...prev, [roomId]: !prev[roomId] }));

  const roomGroups = (diary?.rooms || []).map(room => {
    const sortedBookings = [...room.bookings].sort((a,b) => a.check_in.localeCompare(b.check_in));
    const slotMap = new Map();
    const unassigned = [];
    for (const b of sortedBookings) {
      if (b.room_number) {
        if (!slotMap.has(b.room_number)) slotMap.set(b.room_number, { label: b.room_number, bookings: [] });
        slotMap.get(b.room_number).bookings.push(b);
      } else { unassigned.push(b); }
    }
    let idx = 1;
    for (const b of unassigned) {
      let placed = false;
      for (const [,slot] of slotMap) { if (!slot.bookings.some(ex => b.check_in < ex.check_out && b.check_out > ex.check_in)) { slot.bookings.push(b); placed = true; break; } }
      if (!placed) { const lbl = `#${idx++}`; slotMap.set(lbl, { label: lbl, bookings: [b] }); }
    }
    return { ...room, slots: Array.from(slotMap.values()).filter(s => s.bookings.length > 0) };
  });

  const totalGridWidth = daysCount * COL_W;

  // Scroll to today on mount
  useEffect(() => {
    if (scrollRef.current && todayOffset > 0) {
      scrollRef.current.scrollLeft = Math.max(0, todayOffset * COL_W - 200);
    }
  }, [todayOffset, daysCount]);

  // Infinite scroll — load more days in both directions
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      // Near right edge → load more future days
      if (scrollWidth - scrollLeft - clientWidth < 200 && onLoadMoreRight) {
        onLoadMoreRight();
      }
      // Near left edge → load more past days
      if (scrollLeft < 200 && onLoadMoreLeft) {
        // Remember scroll position so we can restore after prepend
        const prevWidth = scrollWidth;
        onLoadMoreLeft().then?.(() => {
          requestAnimationFrame(() => {
            if (el) el.scrollLeft += el.scrollWidth - prevWidth;
          });
        });
      }
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [onLoadMoreRight, onLoadMoreLeft]);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid="diary-grid">
      <div className="overflow-x-auto" ref={scrollRef}>
        <div className="relative" style={{ minWidth: `${ROOM_COL_W + totalGridWidth}px` }}>

          {/* Header row */}
          <div className="flex border-b border-gray-200 bg-gray-50 sticky top-0 z-20">
            <div className="flex-shrink-0 border-r border-gray-200 bg-gray-50 sticky left-0 z-30" style={{ width: `${ROOM_COL_W}px`, minHeight: '42px' }} />
            <div className="flex">
              {diary?.dates?.map(d => {
                const isT = d.date === today;
                const isWe = ['Sat','Sun'].includes(d.day);
                return (
                  <div key={d.date} className={`text-center border-r border-gray-100 flex-shrink-0 py-2 ${isT ? 'bg-blue-50' : isWe ? 'bg-gray-50/80' : ''}`} style={{ width: `${COL_W}px` }}>
                    <div className={`text-[10px] font-medium uppercase ${isT ? 'text-blue-600' : isWe ? 'text-red-400' : 'text-gray-400'}`}>{d.day}</div>
                    <div className={`text-sm font-bold ${isT ? 'text-blue-700' : 'text-gray-800'}`}>{d.day_num}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {roomGroups.map(room => {
            const isCollapsed = collapsed[room.id];
            return (
              <div key={room.id} data-testid={`room-group-${room.id}`}>
                {/* Room type header with AVL cells */}
                <div className="flex border-b border-gray-200 bg-gray-50/50">
                  <div
                    className="flex-shrink-0 border-r border-gray-200 flex items-center gap-2 px-3 cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50 sticky left-0 z-10"
                    style={{ width: `${ROOM_COL_W}px`, height: '34px' }}
                    onClick={() => toggleCollapse(room.id)}
                    data-testid={`collapse-${room.id}`}
                  >
                    <span className="w-2 h-2 rounded-full bg-gray-800 flex-shrink-0" />
                    <span className="text-[11px] font-bold text-gray-700 truncate flex-1">{room.name}</span>
                    {isCollapsed ? <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" /> : <ChevronUp className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
                  </div>
                  <div className="flex" style={{ height: '34px' }}>
                    {room.days.map(d => <AVLCell key={d.date} booked={d.booked} total={room.total_inventory} isToday={d.date === today} />)}
                  </div>
                </div>

                {/* Individual room slots with booking bars */}
                {!isCollapsed && room.slots.map((slot, si) => (
                  <div key={si} className="flex border-b border-gray-50">
                    <div
                      className="flex-shrink-0 border-r border-gray-200 flex items-center justify-center bg-white sticky left-0 z-10"
                      style={{ width: `${ROOM_COL_W}px`, height: '34px' }}
                    >
                      <span className="text-[11px] font-medium text-gray-500">{slot.label}</span>
                    </div>
                    <div className="flex relative" style={{ height: '34px' }}>
                      {diary?.dates?.map(d => <div key={d.date} className={`flex-shrink-0 border-r border-gray-50 ${d.date === today ? 'bg-blue-50/20' : ''}`} style={{ width: `${COL_W}px` }} />)}
                      {slot.bookings.map(b => <BookingBar key={b.id} booking={b} startDate={startDate} totalDays={daysCount} colWidth={COL_W} onClick={onBookingClick} />)}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}

          {/* Today indicator line */}
          {todayOffset >= 0 && <div className="absolute top-0 bottom-0 w-[2px] bg-red-500 z-30 pointer-events-none" style={{ left: `${ROOM_COL_W + todayOffset*COL_W + COL_W/2}px` }} data-testid="today-line" />}
        </div>
      </div>
    </div>
  );
}

/* ════════════════ Main ════════════════ */
export default function AdminDiary() {
  return <AdminLayout><DiaryContent /></AdminLayout>;
}

function DiaryContent() {
  const { selectedProperty: globalProp, properties: globalProps } = useProperty();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const INITIAL_DAYS = 30;
  const LOAD_MORE_DAYS = 14;

  const [startDate, setStartDate] = useState(addDaysISO(todayISO(), -30));
  const [endDate, setEndDate] = useState(addDaysISO(todayISO(), 30));
  const [diary, setDiary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [walkInSaving, setWalkInSaving] = useState(false);
  const [guestQuery, setGuestQuery] = useState('');
  const [guestResults, setGuestResults] = useState([]);
  const [guestSearching, setGuestSearching] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const guestSearchTimeout = useRef(null);
  const containerRef = useRef(null);
  const [editBooking, setEditBooking] = useState(null);
  const [historyBooking, setHistoryBooking] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [walkInForm, setWalkInForm] = useState({ property_id:'', room_type_id:'', check_in:todayISO(), check_out:'', guest_name:'', guest_email:'', guest_phone:'', guest_country:'', guest_id:'', total_amount:0, currency:'', notes:'' });

  const daysCount = daysBetween(startDate, endDate);
  const incrementalRef = useRef(false);

  useEffect(() => {
    if (globalProps?.length > 0) {
      setProperties(globalProps);
      if (globalProp) setSelectedProperty(globalProp);
      else if (!selectedProperty && globalProps.length > 0) setSelectedProperty(globalProps[0].id);
    }
  }, [globalProps, globalProp]);

  const fetchDiary = useCallback(async (s, e) => {
    if (!selectedProperty) return;
    const days = daysBetween(s || startDate, e || endDate);
    setLoading(true);
    try { const res = await diaryAPI.get(selectedProperty, s || startDate, days); setDiary(res.data); }
    catch { toast.error('Failed to load diary'); }
    finally { setLoading(false); }
  }, [selectedProperty]);

  // Initial fetch + refetch on property change
  useEffect(() => { if (selectedProperty && startDate && endDate) fetchDiary(startDate, endDate); }, [selectedProperty]);
  useEffect(() => { if (selectedProperty) roomsAPI.list(selectedProperty).then(r => setRooms(r.data)); }, [selectedProperty]);

  // Manual date change from pickers
  const handleStartDateChange = (v) => { setStartDate(v); const e = v >= endDate ? addDaysISO(v, INITIAL_DAYS) : endDate; setEndDate(e); fetchDiary(v, e); };
  const handleEndDateChange = (v) => { setEndDate(v); fetchDiary(startDate, v); };

  const goToday = () => { const s = addDaysISO(todayISO(), -30); const e = addDaysISO(todayISO(), 30); setStartDate(s); setEndDate(e); fetchDiary(s, e); };
  const shiftRange = (dir) => { const d = daysBetween(startDate, endDate); const s = addDaysISO(startDate, dir*d); const e = addDaysISO(endDate, dir*d); setStartDate(s); setEndDate(e); fetchDiary(s, e); };

  // Load more days to the RIGHT
  const loadMoreRight = useCallback(async () => {
    if (incrementalRef.current || !selectedProperty) return;
    incrementalRef.current = true;
    const extraStart = endDate;
    const newEnd = addDaysISO(endDate, LOAD_MORE_DAYS);
    try {
      const res = await diaryAPI.get(selectedProperty, extraStart, LOAD_MORE_DAYS);
      const extra = res.data;
      setDiary(prev => {
        if (!prev) return extra;
        return {
          ...prev,
          dates: [...(prev.dates || []), ...(extra.dates || [])],
          rooms: (prev.rooms || []).map(room => {
            const er = (extra.rooms || []).find(r => r.id === room.id);
            if (!er) return room;
            return { ...room, days: [...room.days, ...er.days], bookings: [...room.bookings, ...er.bookings.filter(b => !room.bookings.some(eb => eb.id === b.id))] };
          }),
        };
      });
      setEndDate(newEnd);
    } catch { /* silent */ }
    finally { incrementalRef.current = false; }
  }, [endDate, selectedProperty]);

  // Load more days to the LEFT
  const loadMoreLeft = useCallback(async () => {
    if (incrementalRef.current || !selectedProperty) return;
    incrementalRef.current = true;
    const newStart = addDaysISO(startDate, -LOAD_MORE_DAYS);
    try {
      const res = await diaryAPI.get(selectedProperty, newStart, LOAD_MORE_DAYS);
      const extra = res.data;
      setDiary(prev => {
        if (!prev) return extra;
        return {
          ...prev,
          dates: [...(extra.dates || []), ...(prev.dates || [])],
          rooms: (prev.rooms || []).map(room => {
            const er = (extra.rooms || []).find(r => r.id === room.id);
            if (!er) return room;
            return { ...room, days: [...er.days, ...room.days], bookings: [...room.bookings, ...er.bookings.filter(b => !room.bookings.some(eb => eb.id === b.id))] };
          }),
        };
      });
      setStartDate(newStart);
    } catch { /* silent */ }
    finally { incrementalRef.current = false; }
  }, [startDate, selectedProperty]);
  const toggleFullscreen = () => { if (!isFullscreen) containerRef.current?.requestFullscreen?.(); else document.exitFullscreen?.(); setIsFullscreen(!isFullscreen); };
  const handleEditSave = async (id, data) => { await bookingsAPI.amend(id, data); await fetchDiary(startDate, endDate); };

  const openWalkIn = () => {
    const prop = properties.find(p => p.id === selectedProperty);
    setWalkInForm({ property_id:selectedProperty, room_type_id:'', check_in:todayISO(), check_out:'', guest_name:'', guest_email:'', guest_phone:'', guest_country:'', guest_id:'', total_amount:0, currency: prop?.currency || 'USD', notes:'' });
    setGuestQuery(''); setGuestResults([]); setSelectedGuest(null); setWalkInOpen(true);
  };
  const handleWalkInSubmit = async (e) => {
    e.preventDefault();
    if (!walkInForm.check_out) { toast.error('Please select check-out date'); return; }
    setWalkInSaving(true);
    try { const res = await walkInAPI.create(walkInForm); toast.success('Booking created!', { description: `Ref: ${res.data.booking_reference}` }); setWalkInOpen(false); fetchDiary(startDate, endDate); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to create booking'); }
    finally { setWalkInSaving(false); }
  };
  const handleGuestSearch = (q) => {
    setGuestQuery(q); setWalkInForm(prev => ({ ...prev, guest_name:q, guest_id:'' })); setSelectedGuest(null);
    clearTimeout(guestSearchTimeout.current);
    if (q.length < 2) { setGuestResults([]); return; }
    guestSearchTimeout.current = setTimeout(async () => {
      setGuestSearching(true);
      try { const res = await guestsAPI.search(walkInForm.property_id, q); setGuestResults(res.data.guests || []); } catch { setGuestResults([]); } finally { setGuestSearching(false); }
    }, 300);
  };
  const selectGuest = (guest) => {
    setSelectedGuest(guest); setGuestQuery(guest.name); setGuestResults([]);
    setWalkInForm(prev => ({ ...prev, guest_name:guest.name, guest_email:guest.email||'', guest_phone:guest.phone||'', guest_country:guest.country||'', guest_id:guest.id }));
  };
  useEffect(() => {
    if (walkInForm.check_in && walkInForm.check_out && walkInForm.room_type_id) {
      const nights = Math.max(1, daysBetween(walkInForm.check_in, walkInForm.check_out));
      const room = rooms.find(r => r.id === walkInForm.room_type_id);
      if (room) {
        let rate = room.price_per_night;
        if (walkInForm.currency !== room.currency && room.currency_rates?.[walkInForm.currency]) rate = room.currency_rates[walkInForm.currency];
        setWalkInForm(prev => ({ ...prev, total_amount: rate * nights }));
      }
    }
  }, [walkInForm.check_in, walkInForm.check_out, walkInForm.room_type_id, rooms]);

  return (
    <div className="page-wrapper" ref={containerRef} data-testid="diary-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Diary</h1>
          <p className="text-sm text-muted-foreground mt-1">Room availability and booking timeline.</p>
        </div>
      </div>

      {/* Top Bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {!globalProp && properties.length > 1 && (
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-[200px]" data-testid="diary-property-select"><SelectValue placeholder="Select Property" /></SelectTrigger>
              <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid="diary-filter"><SlidersHorizontal className="h-4 w-4 text-gray-500" /></button>
          <button onClick={goToday} className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors" data-testid="diary-view-today">Today</button>
          {(startDate || endDate) && (
            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1 shadow-sm ml-2">
              <input type="date" value={startDate} onChange={e => handleStartDateChange(e.target.value)} className="text-xs font-medium text-gray-800 border-0 outline-none bg-transparent w-[110px]" data-testid="diary-start-date" />
              <span className="text-gray-300 text-xs">—</span>
              <input type="date" value={endDate} min={addDaysISO(startDate, 1)} onChange={e => handleEndDateChange(e.target.value)} className="text-xs font-medium text-gray-800 border-0 outline-none bg-transparent w-[110px]" data-testid="diary-end-date" />
              <button onClick={() => { const s = addDaysISO(todayISO(), -30); const e = addDaysISO(todayISO(), 30); setStartDate(s); setEndDate(e); setDiary(null); fetchDiary(s, e); }} className="p-0.5 hover:bg-gray-100 rounded transition-colors" data-testid="diary-clear-dates" title="Reset dates">
                <X className="h-3.5 w-3.5 text-gray-400" />
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shiftRange(-1)} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors" data-testid="diary-prev"><ChevronLeft className="h-5 w-5 text-gray-500" /></button>
          <button onClick={() => shiftRange(1)} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors" data-testid="diary-next"><ChevronRight className="h-5 w-5 text-gray-500" /></button>
          <Button size="sm" onClick={openWalkIn} data-testid="walk-in-btn"><PlusCircle className="h-4 w-4 mr-1.5" /> New Booking</Button>
          <button onClick={toggleFullscreen} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid="diary-fullscreen"><Maximize2 className="h-4 w-4 text-gray-500" /></button>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>
      ) : diary ? (
        <TimelineGrid diary={diary} startDate={startDate} daysCount={daysCount} onBookingClick={(b) => setActionModal({ booking: b, tab: 'guest_info' })} onLoadMoreRight={loadMoreRight} onLoadMoreLeft={loadMoreLeft} />
      ) : (
        <div className="text-center py-24 text-gray-400"><p className="font-medium">Select a property to view the diary</p></div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-5 flex-wrap text-xs text-gray-600 pt-1" data-testid="diary-legend">
        <span className="font-semibold text-gray-700">Bookings:</span>
        {Object.entries({ past: 'Guest has left', future: 'Future booking', current: 'Currently staying', expired: 'Expired' }).map(([k,v]) => (
          <div key={k} className="flex items-center gap-1.5"><span className="w-5 h-3 rounded-sm" style={{ background: BAR_COLORS[k].bg, border: `1px solid ${BAR_COLORS[k].border}` }} /><span>{v}</span></div>
        ))}
        <span className="ml-3 font-semibold text-gray-700">AVL:</span>
        <div className="flex items-center gap-1.5"><span className="text-[11px] font-semibold text-gray-600">2/4</span><span>Normal</span></div>
        <div className="flex items-center gap-1.5"><span className="text-[11px] font-bold text-red-500 bg-orange-50 px-1 rounded">4/4</span><span>Full</span></div>
        <div className="flex items-center gap-1.5"><span className="text-[11px] font-bold text-green-600 bg-red-50 px-1 rounded">5/4</span><span>Overbooked</span></div>
      </div>

      {/* Unified Booking Action Modal */}
      {actionModal && (
        <BookingActionModal
          booking={actionModal.booking}
          initialTab={actionModal.tab}
          properties={properties}
          propertyTimezone={(() => { const p = properties.find(x => x.id === selectedProperty); return p?.timezone; })()}
          onAmend={handleEditSave}
          onCheckout={(id) => bookingsAPI.checkout(id)}
          onTransfer={(id, data) => bookingsAPI.transfer(id, data)}
          onClose={() => setActionModal(null)}
          onRefresh={() => fetchDiary(startDate, endDate)}
        />
      )}

      {/* Walk-in Dialog */}
      <Dialog open={walkInOpen} onOpenChange={setWalkInOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><PlusCircle className="h-5 w-5" /> New Booking</DialogTitle></DialogHeader>
          <form onSubmit={handleWalkInSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Property</Label>
                <Select value={walkInForm.property_id} onValueChange={v => { setWalkInForm(prev => ({ ...prev, property_id:v, room_type_id:'' })); roomsAPI.list(v).then(r => setRooms(r.data)); }}>
                  <SelectTrigger data-testid="walkin-property"><SelectValue placeholder="Property" /></SelectTrigger>
                  <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Room Type *</Label>
                <Select value={walkInForm.room_type_id} onValueChange={v => { const room = rooms.find(r => r.id === v); setWalkInForm(prev => ({ ...prev, room_type_id:v, currency:room?.currency||prev.currency||'USD', total_amount:room?.price_per_night||0 })); }}>
                  <SelectTrigger data-testid="walkin-room"><SelectValue placeholder="Select room" /></SelectTrigger>
                  <SelectContent>{rooms.filter(r => r.property_id === walkInForm.property_id).map(r => <SelectItem key={r.id} value={r.id}>{r.name} — {getCurrencySymbol(r.currency||'USD')}{r.price_per_night}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Check-in *</Label><Input type="date" value={walkInForm.check_in} onChange={e => setWalkInForm(prev => ({ ...prev, check_in:e.target.value }))} required data-testid="walkin-checkin" /></div>
              <div className="space-y-1.5"><Label>Check-out *</Label><Input type="date" value={walkInForm.check_out} min={walkInForm.check_in} onChange={e => setWalkInForm(prev => ({ ...prev, check_out:e.target.value }))} required data-testid="walkin-checkout" /></div>
            </div>
            <div className="space-y-1.5 relative">
              <Label>Guest Name *</Label>
              <Input value={guestQuery} onChange={e => handleGuestSearch(e.target.value)} placeholder="Search guests..." required autoComplete="off" data-testid="walkin-name" />
              {selectedGuest && <p className="text-xs text-blue-600 mt-0.5">Linked: {selectedGuest.name}</p>}
              {guestResults.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 border rounded-md bg-white shadow-lg max-h-40 overflow-y-auto" data-testid="guest-suggestions">
                  {guestResults.map(g => <button key={g.id} type="button" className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b last:border-0" onClick={() => selectGuest(g)}><span className="font-medium">{g.name}</span><span className="text-gray-400 ml-2">{g.email}</span></button>)}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={walkInForm.guest_email} onChange={e => setWalkInForm(prev => ({ ...prev, guest_email:e.target.value }))} data-testid="walkin-email" /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={walkInForm.guest_phone} onChange={e => setWalkInForm(prev => ({ ...prev, guest_phone:e.target.value }))} data-testid="walkin-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Currency *</Label>
                <Select value={walkInForm.currency} onValueChange={code => {
                  const room = rooms.find(r => r.id === walkInForm.room_type_id);
                  const nights = walkInForm.check_in && walkInForm.check_out ? Math.max(1, daysBetween(walkInForm.check_in, walkInForm.check_out)) : 1;
                  let rate = room?.price_per_night || 0;
                  if (room?.currency_rates?.[code]) rate = room.currency_rates[code];
                  setWalkInForm(prev => ({ ...prev, currency:code, total_amount: rate*nights }));
                }}>
                  <SelectTrigger data-testid="walkin-currency"><SelectValue placeholder="Currency" /></SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const room = rooms.find(r => r.id === walkInForm.room_type_id);
                      const rates = [];
                      if (room) { rates.push({ code:room.currency||'USD', rate:room.price_per_night, def:true }); if (room.currency_rates) Object.entries(room.currency_rates).forEach(([c,r]) => { if (c !== room.currency) rates.push({ code:c, rate:r }); }); }
                      return rates.length > 0 ? rates.map(r => <SelectItem key={r.code} value={r.code}>{getCurrencySymbol(r.code)} {r.code} — {r.rate}/night{r.def ? ' (default)' : ''}</SelectItem>) : <SelectItem value={walkInForm.currency||'USD'}>{walkInForm.currency||'USD'}</SelectItem>;
                    })()}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Total Amount *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{getCurrencySymbol(walkInForm.currency||'USD')}</span>
                  <Input type="number" min="0" step="0.01" value={walkInForm.total_amount} onChange={e => setWalkInForm(prev => ({ ...prev, total_amount:parseFloat(e.target.value)||0 }))} className="pl-8" required data-testid="walkin-amount" />
                </div>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={walkInForm.notes} onChange={e => setWalkInForm(prev => ({ ...prev, notes:e.target.value }))} placeholder="Special requests..." rows={2} data-testid="walkin-notes" /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setWalkInOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={walkInSaving} data-testid="walkin-submit">{walkInSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create Booking</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
